import { QuestionType, Question } from '../types'
import {
  QuestionTypeConfig,
  QuestionRendererProps,
} from '../../practice/components/renderers/types'
import { getValidator } from './validatorRegistry'

// Renderers
import { MultipleChoiceRenderer } from '../../practice/components/renderers/MultipleChoiceRenderer'
import { ColumnArithmeticRenderer } from '../../practice/components/renderers/ColumnArithmeticRenderer'
import { LongDivisionRenderer } from '../../practice/components/renderers/LongDivisionRenderer'
import { MoneyAssemblerRenderer } from '../../practice/components/renderers/MoneyAssemblerRenderer'
import { ClockRenderer } from '../../practice/components/renderers/ClockRenderer'
import { FillInRenderer } from '../../practice/components/renderers/FillInRenderer'
import { OrderingRenderer } from '../../practice/components/renderers/OrderingRenderer'
import { MatchingRenderer } from '../../practice/components/renderers/MatchingRenderer'
import { DragDropFillRenderer } from '../../practice/components/renderers/DragDropFillRenderer'
import { TableReadingRenderer } from '../../practice/components/renderers/TableReadingRenderer'
import { NumberSequenceRenderer } from '../../practice/components/renderers/NumberSequenceRenderer'
import { RatioTableRenderer } from '../../practice/components/renderers/RatioTableRenderer'
import { TextHighlightRenderer } from '../../practice/components/renderers/TextHighlightRenderer'
import { BarChartRenderer } from '../../practice/components/renderers/BarChartRenderer'
import { LineChartRenderer } from '../../practice/components/renderers/LineChartRenderer'
import { ScheduleRenderer } from '../../practice/components/renderers/ScheduleRenderer'
import { PieChartRenderer } from '../../practice/components/renderers/PieChartRenderer'
import { MultiplicationGridRenderer } from '../../practice/components/renderers/MultiplicationGridRenderer'
import { TrueFalseSeriesRenderer } from '../../practice/components/renderers/TrueFalseSeriesRenderer'
import { EstimationSliderRenderer } from '../../practice/components/renderers/EstimationSliderRenderer'
import { CategorizeRenderer } from '../../practice/components/renderers/CategorizeRenderer'
import { ErrorSpottingRenderer } from '../../practice/components/renderers/ErrorSpottingRenderer'

/**
 * Question Type Registry v3.0
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/config/questionTypeRegistry.ts in R25.
 *
 * Modern architecture with clean separation of concerns:
 * - questionTypeRegistry.ts → renderer mapping + metadata
 * - validatorRegistry.ts → all validation logic
 * - answerProtocol.ts → answer payload types + bridge functions
 */

/** Metadata-only config for a question type (no validation logic) */
interface QuestionTypeMeta {
  type: QuestionType
  label: string
  isInteractive: boolean
  hasBuiltInVisuals: boolean
  shouldShuffleOptions: boolean
  isCitoEligible: boolean
  Renderer: React.ComponentType<QuestionRendererProps>
}
const QUESTION_TYPE_META: Record<QuestionType, QuestionTypeMeta> = {
  multiple_choice: {
    type: 'multiple_choice',
    label: 'Meerkeuze',
    isInteractive: false,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: true,
    Renderer: MultipleChoiceRenderer,
  },
  cijferend_optellen: {
    type: 'cijferend_optellen',
    label: 'Cijferend Optellen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: ColumnArithmeticRenderer,
  },
  cijferend_aftrekken: {
    type: 'cijferend_aftrekken',
    label: 'Cijferend Aftrekken',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: ColumnArithmeticRenderer,
  },
  cijferend_vermenigvuldigen: {
    type: 'cijferend_vermenigvuldigen',
    label: 'Cijferend Vermenigvuldigen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: ColumnArithmeticRenderer,
  },
  cijferend_delen: {
    type: 'cijferend_delen',
    label: 'Cijferend Delen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: LongDivisionRenderer,
  },
  clock: {
    type: 'clock',
    label: 'Klokkijken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: ClockRenderer,
  },
  money: {
    type: 'money',
    label: 'Geld',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: MoneyAssemblerRenderer,
  },
  fill_in: {
    type: 'fill_in',
    label: 'Invulvraag',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: FillInRenderer,
  },
  ordering: {
    type: 'ordering',
    label: 'Volgorde',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: OrderingRenderer,
  },
  matching: {
    type: 'matching',
    label: 'Koppelen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: MatchingRenderer,
  },
  drag_drop_fill: {
    type: 'drag_drop_fill',
    label: 'Sleep en Plaats',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: false,
    Renderer: DragDropFillRenderer,
  },
  table_reading: {
    type: 'table_reading',
    label: 'Tabel Aflezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: TableReadingRenderer,
  },
  number_sequence: {
    type: 'number_sequence',
    label: 'Getallenreeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: NumberSequenceRenderer,
  },
  ratio_table: {
    type: 'ratio_table',
    label: 'Verhoudingstabel',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: RatioTableRenderer,
  },
  text_highlight: {
    type: 'text_highlight',
    label: 'Tekst Markeren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: TextHighlightRenderer,
  },
  bar_chart: {
    type: 'bar_chart',
    label: 'Staafgrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: BarChartRenderer,
  },
  line_chart: {
    type: 'line_chart',
    label: 'Lijngrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: LineChartRenderer,
  },
  schedule_reading: {
    type: 'schedule_reading',
    label: 'Rooster Lezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: ScheduleRenderer,
  },
  pie_chart: {
    type: 'pie_chart',
    label: 'Cirkeldiagram',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: PieChartRenderer,
  },
  multiplication_grid: {
    type: 'multiplication_grid',
    label: 'Tafelrooster',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: MultiplicationGridRenderer,
  },
  true_false_series: {
    type: 'true_false_series',
    label: 'Waar/Onwaar Reeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: TrueFalseSeriesRenderer,
  },
  estimation_slider: {
    type: 'estimation_slider',
    label: 'Schatting',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: EstimationSliderRenderer,
  },
  categorize: {
    type: 'categorize',
    label: 'Sorteren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: false,
    Renderer: CategorizeRenderer,
  },
  error_spotting: {
    type: 'error_spotting',
    label: 'Fouten Zoeken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    Renderer: ErrorSpottingRenderer,
  },
}

/**
 * Build the QUESTION_TYPE_REGISTRY by combining
 * metadata from QUESTION_TYPE_META with validators from ValidatorRegistry.
 */
export const QUESTION_TYPE_REGISTRY: Record<QuestionType, QuestionTypeConfig> =
  Object.fromEntries(
    Object.entries(QUESTION_TYPE_META).map(([key, meta]) => [
      key,
      {
        ...meta,
        validateAnswer: (userAnswer: string | number, question: Question) =>
          getValidator(key)(userAnswer, question),
      },
    ]),
  ) as Record<QuestionType, QuestionTypeConfig>
export function getQuestionTypeConfig(type: string): QuestionTypeConfig {
  if (type === 'geld_samenstellen' || type === 'klok_aflezen') {
    throw new Error(
      `Obsolete questionType "${type}" detected. Migrate to canonical "money" or "clock".`,
    )
  }
  const config = QUESTION_TYPE_REGISTRY[type as QuestionType]
  if (!config) {
    throw new Error(
      `Unsupported questionType "${type}" in questionTypeRegistry.`,
    )
  }
  return config
}
